import { z } from "zod";
import { paginationSchema } from "./common.js";
import { BILLING_PLAN_TIERS } from "../billing/index.js";

export const billingPlanTierSchema = z.enum(BILLING_PLAN_TIERS);

export const billingSubscriptionStatusSchema = z.enum([
  "TRIALING",
  "ACTIVE",
  "PAST_DUE",
  "CANCELED",
  "PAUSED",
]);

export const billingSubscriptionSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  planTier: billingPlanTierSchema,
  status: billingSubscriptionStatusSchema,
  provider: z.string(),
  trialEndsAt: z.string().nullable(),
  currentPeriodStart: z.string().nullable(),
  currentPeriodEnd: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const billingPlanSchema = z.object({
  tier: billingPlanTierSchema,
  label: z.string(),
  pricePerWorkerCents: z.number().int().nullable(),
  description: z.string(),
});

export const billingPayPeriodRefSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  status: z.string(),
});

export const billingEstimateSchema = z.object({
  activeWorkers: z.number().int(),
  unitPriceCents: z.number().int().nullable(),
  estimatedAmountCents: z.number().int().nullable(),
  currency: z.string(),
  isCustomPricing: z.boolean(),
});

export const billingSummarySchema = z.object({
  subscription: billingSubscriptionSchema,
  plan: billingPlanSchema,
  currentPayPeriod: billingPayPeriodRefSchema.nullable(),
  currentPeriodUsage: billingEstimateSchema,
});

export const billingUsageRowSchema = z.object({
  payPeriodId: z.string().uuid(),
  payPeriodLabel: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  payPeriodStatus: z.string(),
  activeWorkers: z.number().int(),
  unitPriceCents: z.number().int().nullable(),
  estimatedAmountCents: z.number().int().nullable(),
  planTier: billingPlanTierSchema,
  isSnapshot: z.boolean(),
  snapshotId: z.string().uuid().nullable(),
  snapshotAt: z.string().nullable(),
});

export const billingUsageListQuerySchema = paginationSchema.extend({
  payPeriodId: z.string().uuid().optional(),
});

export const createBillingSnapshotSchema = z.object({
  payPeriodId: z.string().uuid(),
});

export const updateBillingSubscriptionSchema = z.object({
  planTier: billingPlanTierSchema,
});

export type BillingSubscription = z.infer<typeof billingSubscriptionSchema>;
export type BillingPlan = z.infer<typeof billingPlanSchema>;
export type BillingPayPeriodRef = z.infer<typeof billingPayPeriodRefSchema>;
export type BillingSummary = z.infer<typeof billingSummarySchema>;
export type BillingUsageRow = z.infer<typeof billingUsageRowSchema>;
export type BillingEstimate = z.infer<typeof billingEstimateSchema>;
export type BillingPlanTier = z.infer<typeof billingPlanTierSchema>;
